/**
 * Floating Product Carousel — coverflow-style tilted product slider
 * Vanilla JS, no dependencies. One instance per section (theme-editor safe).
 */
(function () {
  'use strict';

  function initFloatingCarousel(root) {
    var track = root.querySelector('[data-fpc-track]');
    if (!track) return;

    var items = Array.prototype.slice.call(track.querySelectorAll('[data-fpc-item]'));
    if (!items.length) return;

    var titleEl = root.querySelector('[data-fpc-title]');
    var progressFill = root.querySelector('[data-fpc-progress-fill]');

    var total = items.length;
    var activeIndex = 0;

    var tiltDeg = parseFloat(root.dataset.tiltDeg || '32');
    var maxTilt = parseFloat(root.dataset.maxTilt || '55');
    var stepXPercent = parseFloat(root.dataset.stepX || '34') / 100; // % of item width
    var scaleStep = parseFloat(root.dataset.scaleStep || '0.14');
    var maxVisible = parseInt(root.dataset.maxVisible || '3', 10);
    var autoplay = root.dataset.autoplay === 'true';
    var autoplaySpeed = parseInt(root.dataset.autoplaySpeed || '4000', 10);

    var dragging = false;
    var dragStartX = 0;
    var dragDeltaX = 0;
    var pointerId = null;
    var autoplayTimer = null;
    var itemWidthPx = 0;
    var resizeRaf = null;

    function measureItemWidth() {
      itemWidthPx = items[0].getBoundingClientRect().width || itemWidthPx;
    }

    function signedIndex(i) {
      var diff = (i - activeIndex) % total;
      if (diff < 0) diff += total;
      if (diff > total / 2) diff -= total;
      return diff;
    }

    function clamp(value, min, max) {
      return Math.max(min, Math.min(max, value));
    }

    function applyTransform(item, signed, extraPx) {
      extraPx = extraPx || 0;
      var abs = Math.abs(signed);
      var visible = abs <= maxVisible;
      var x = stepXPercent * itemWidthPx * signed;
      var tilt = clamp(-signed * tiltDeg, -maxTilt, maxTilt);
      var scale = Math.max(1 - scaleStep * abs, 0.3);
      var z = -abs * 60;
      var opacity = visible ? 1 : 0;
      var zIndex = total - abs;

      item.style.transform =
        'translate3d(' +
        (x + extraPx) +
        'px, 0px, ' +
        z +
        'px) rotateY(' +
        tilt +
        'deg) scale(' +
        scale +
        ')';
      item.style.opacity = String(opacity);
      item.style.zIndex = String(zIndex);
      item.setAttribute('data-active', signed === 0 ? 'true' : 'false');

      var link = item.querySelector('[data-fpc-link]');
      if (link) link.setAttribute('tabindex', signed === 0 ? '0' : '-1');
    }

    function updateCaption() {
      var active = items[activeIndex];
      if (!active) return;
      var title = active.dataset.title || '';

      if (titleEl) {
        titleEl.style.opacity = '0';
        window.setTimeout(function () {
          titleEl.textContent = title;
          titleEl.style.opacity = '1';
        }, 120);
      }

      if (progressFill) {
        var pct = total > 1 ? (activeIndex / (total - 1)) * 100 : 0;
        progressFill.style.width = pct + '%';
      }
    }

    function render(extraPx) {
      items.forEach(function (item, i) {
        applyTransform(item, signedIndex(i), extraPx);
      });
    }

    function goTo(index) {
      activeIndex = ((index % total) + total) % total;
      render(0);
      updateCaption();
    }

    function next() {
      goTo(activeIndex + 1);
    }

    function prev() {
      goTo(activeIndex - 1);
    }

    // ---- Autoplay ----

    function startAutoplay() {
      if (!autoplay || total < 2) return;
      stopAutoplay();
      autoplayTimer = window.setInterval(next, autoplaySpeed);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    // ---- Drag / swipe ----

    function onPointerDown(e) {
      if (total < 2) return;
      dragging = true;
      pointerId = e.pointerId;
      dragStartX = e.clientX;
      dragDeltaX = 0;
      track.classList.add('is-dragging');
      stopAutoplay();
      try {
        track.setPointerCapture(pointerId);
      } catch (err) {
        /* noop */
      }
    }

    function onPointerMove(e) {
      if (!dragging) return;
      dragDeltaX = e.clientX - dragStartX;
      render(dragDeltaX * 0.6);
    }

    function onPointerUp() {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');

      var threshold = 60;
      if (dragDeltaX <= -threshold) {
        next();
      } else if (dragDeltaX >= threshold) {
        prev();
      } else {
        render(0);
      }
      dragDeltaX = 0;
      startAutoplay();
    }

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', onPointerUp);
    track.addEventListener('pointercancel', onPointerUp);
    track.addEventListener('pointerleave', function (e) {
      if (dragging && e.pointerId === pointerId) onPointerUp();
    });

    track.addEventListener(
      'click',
      function (e) {
        if (Math.abs(dragDeltaX) > 5) {
          e.preventDefault();
          return;
        }
        var item = e.target.closest ? e.target.closest('[data-fpc-item]') : null;
        if (item && item.dataset.index !== undefined) {
          var idx = parseInt(item.dataset.index, 10);
          if (idx !== activeIndex) {
            e.preventDefault();
            goTo(idx);
            stopAutoplay();
            startAutoplay();
          }
        }
      },
      true
    );

    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      }
    });

    window.addEventListener('resize', function () {
      if (resizeRaf) window.cancelAnimationFrame(resizeRaf);
      resizeRaf = window.requestAnimationFrame(function () {
        measureItemWidth();
        render(0);
      });
    });

    measureItemWidth();
    render(0);
    updateCaption();
    startAutoplay();

    document.addEventListener('shopify:section:unload', function (e) {
      if (e.target === root) stopAutoplay();
    });
    document.addEventListener('shopify:section:select', function (e) {
      if (root.contains(e.target)) stopAutoplay();
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (root.contains(e.target)) startAutoplay();
    });
    document.addEventListener('shopify:block:select', function (e) {
      var item = e.target.closest ? e.target.closest('[data-fpc-item]') : null;
      if (item && root.contains(item)) {
        stopAutoplay();
        goTo(parseInt(item.dataset.index, 10));
      }
    });
  }

  function initAll() {
    document.querySelectorAll('[data-fpc-root]').forEach(initFloatingCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-fpc-root]');
    if (root) initFloatingCarousel(root);
  });
})();
